import React, { useEffect, useMemo, useRef, useState } from 'react';
import Plotly from 'plotly.js-dist-min';
import { useAdminUsageMetrics } from '../../hooks/useAdminUsageMetrics.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const UsageMetricsSection = ({ onNotify }) => {
  const {
    filters,
    draftFilters,
    rows,
    summary,
    loading,
    error,
    loadMetrics,
    setFilterValue,
    exportMetrics,
    trends,
    catalogs
  } = useAdminUsageMetrics();

  useLucideIcons([
    rows.length,
    summary.totalClicks,
    loading,
    filters.aggregate,
    trends.labels?.length,
    trends.divisionBreakdown?.length,
    trends.areaBreakdown?.length,
    trends.processTypeBreakdown?.articulos?.length,
    trends.processTypeBreakdown?.recetas?.length,
    filters.divisionId,
    filters.areaId
  ]);

  const handleFiltersSubmit = async (event) => {
    event.preventDefault();
    try {
      await loadMetrics();
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudieron aplicar los filtros.' });
    }
  };

  const handleExport = async () => {
    try {
      await exportMetrics();
      onNotify?.({ type: 'success', message: 'Se inició la descarga del archivo.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo exportar el archivo.' });
    }
  };

  const processColumns = useMemo(() => {
    const names = new Set();
    rows.forEach((row) => {
      const counts = row?.procesos_seleccionados_counts;
      if (counts && typeof counts === 'object' && !Array.isArray(counts)) {
        Object.keys(counts).forEach((name) => {
          if (name) {
            names.add(name);
          }
        });
      }
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const tableColumns = useMemo(() => {
    const columns = [];
    if (!filters.aggregate) {
      columns.push({ key: 'fecha', label: 'Fecha', getValue: (row) => row.fecha || '' });
    }
    columns.push(
      { key: 'user_name', label: 'Usuario', getValue: (row) => row.user_name || 'Sin nombre' },
      { key: 'sucursal_nombre', label: 'Restaurante', getValue: (row) => row.sucursal_nombre || '-' },
      { key: 'rbi_id', label: 'RBI ID', getValue: (row) => row.rbi_id || '-', exportOnly: true },
      { key: 'division_nombre', label: 'División', getValue: (row) => row.division_nombre || '-', exportOnly: true },
      { key: 'area_nombre', label: 'Área', getValue: (row) => row.area_nombre || '-', exportOnly: true },
      { key: 'articulos', label: 'Click Artículos', getValue: (row) => row.articulos || 0 },
      { key: 'recetas', label: 'Click Recetas', getValue: (row) => row.recetas || 0 },
      { key: 'total', label: 'Total', getValue: (row) => row.total || 0 },
      ...processColumns.map((processName) => ({
        key: `process:${processName}`,
        label: processName,
        getValue: (row) => {
          const count = row?.procesos_seleccionados_counts?.[processName];
          return Number.isFinite(Number(count)) ? Number(count) : 0;
        }
      })),
      {
        key: 'procesos_seleccionados',
        label: 'Procesos seleccionados',
        getValue: (row) => {
          const procesos = row.procesos_seleccionados || [];
          return Array.isArray(procesos) ? procesos.join(', ') : '';
        },
        exportOnly: true
      }
    );
    return columns;
  }, [filters.aggregate, processColumns]);

  const tableColumnsVisible = tableColumns.filter(col => !col.exportOnly);
  const tableHeaders = tableColumnsVisible.map((column) => column.label);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 50;
  const filteredRows = useMemo(() => {
    const normalizedQuery = searchTerm.trim().toLowerCase();
    if (!normalizedQuery) {
      return rows;
    }
    return rows.filter((row) => {
      const haystack = [
        row.fecha,
        row.user_name,
        row.sucursal_nombre,
        row.rbi_id
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [rows, searchTerm]);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredRows.slice(startIndex, startIndex + rowsPerPage);
  }, [currentPage, filteredRows]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredRows.length, filters.aggregate, filters.startDate, filters.endDate, searchTerm]);

  const filteredAreas = useMemo(() => {
    if (!draftFilters.divisionId) {
      return catalogs.areas;
    }
    const selectedDivision = catalogs.divisions.find(
      (division) => String(division.boh) === String(draftFilters.divisionId)
    );
    if (!selectedDivision) {
      return [];
    }
    return catalogs.areas.filter((area) => Number(area.division_id) === Number(selectedDivision.id));
  }, [catalogs.areas, catalogs.divisions, draftFilters.divisionId]);

  useEffect(() => {
    if (!draftFilters.areaId) {
      return;
    }
    const currentArea = catalogs.areas.find((area) => String(area.boh) === String(draftFilters.areaId));
    if (!currentArea) {
      setFilterValue('areaId', '');
      return;
    }
    if (draftFilters.divisionId) {
      const selectedDivision = catalogs.divisions.find(
        (division) => String(division.boh) === String(draftFilters.divisionId)
      );
      if (!selectedDivision || String(currentArea.division_id) !== String(selectedDivision.id)) {
        setFilterValue('areaId', '');
      }
    }
  }, [catalogs.areas, catalogs.divisions, draftFilters.areaId, draftFilters.divisionId, setFilterValue]);

  const handleTableExport = () => {
    if (!rows.length) {
      onNotify?.({ type: 'error', message: 'No hay filas para exportar.' });
      return;
    }

    const escapeCsv = (value) => {
      const text = value === null || value === undefined ? '' : String(value);
      if (/[",\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };

    const lines = [
      tableColumns.map((column) => escapeCsv(column.label)).join(',')
    ];

    rows.forEach((row) => {
      const line = tableColumns.map((column) => escapeCsv(column.getValue(row))).join(',');
      lines.push(line);
    });

    const content = `\uFEFF${lines.join('\n')}`;
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `metricas_detalle_${filters.startDate}_${filters.endDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <section id="metrics-section" className="admin-section active usage-metrics-section">
      <div className="section-header metrics-header">
        <div className="section-title-group">
          <div className="section-title-line">
            <h2 className="section-title">Métricas de Uso</h2>
          </div>
        </div>
      </div>

      <div className="metrics-stack">
        <div className="metrics-filters-inline">
          <form className="usage-filters-classic" onSubmit={handleFiltersSubmit}>
            <div className="filters-row filters-row-dates">
              <div className="form-group">
                <label className="form-label">Fecha inicio</label>
                <input
                  type="date"
                  className="form-input"
                  value={draftFilters.startDate}
                  onChange={(event) => setFilterValue('startDate', event.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Fecha fin</label>
                <input
                  type="date"
                  className="form-input"
                  value={draftFilters.endDate}
                  onChange={(event) => setFilterValue('endDate', event.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">División</label>
                <select
                  className="form-select metrics-filter-select"
                  value={draftFilters.divisionId}
                  onChange={(event) => setFilterValue('divisionId', event.target.value)}
                  disabled={catalogs.loading}
                >
                  <option value="">Todas</option>
                  {catalogs.divisions.map((division) => (
                    <option key={division.id} value={division.boh}>
                      {division.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Área</label>
                <select
                  className="form-select metrics-filter-select"
                  value={draftFilters.areaId}
                  onChange={(event) => setFilterValue('areaId', event.target.value)}
                  disabled={catalogs.loading || !filteredAreas.length}
                >
                  <option value="">Todas</option>
                  {filteredAreas.map((area) => (
                    <option key={area.id} value={area.boh}>
                      {area.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Restaurante</label>
                <select
                  className="form-select metrics-filter-select"
                  value={draftFilters.restaurantId}
                  onChange={(event) => setFilterValue('restaurantId', event.target.value)}
                  disabled={catalogs.loading}
                >
                  <option value="">Todos</option>
                  {catalogs.restaurants.map((restaurant) => (
                    <option key={restaurant.id_boh} value={restaurant.id_boh}>
                      {restaurant.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="filters-row filters-row-actions">
              <button className="btn-primary" type="submit" disabled={loading}>
                {loading ? 'Consultando...' : 'Aplicar filtros'}
              </button>
              <button className="btn-secondary" type="button" onClick={handleExport} disabled={loading}>
                <span className="btn-icon"><i data-lucide="download" /></span>
                Exportar
              </button>
            </div>
            <div className="filters-row filters-row-options">
              <div className="form-group checkbox-group stacked">
                <div className="checkbox-title">Pronóstico incluido</div>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={draftFilters.includeArticulos}
                    onChange={(event) => setFilterValue('includeArticulos', event.target.checked)}
                  />
                  <span className="checkmark" />
                  <span className="checkbox-label">Artículos</span>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={draftFilters.includeRecetas}
                    onChange={(event) => setFilterValue('includeRecetas', event.target.checked)}
                  />
                  <span className="checkmark" />
                  <span className="checkbox-label">Recetas</span>
                </label>
              </div>
              <div className="form-group checkbox-group stacked">
                <div className="checkbox-title">Modo de lectura</div>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={draftFilters.aggregate}
                    onChange={(event) => setFilterValue('aggregate', event.target.checked)}
                  />
                  <span className="checkmark" />
                  <span className="checkbox-label">Agrupar por usuario</span>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={draftFilters.forecastDetail}
                    onChange={(event) => setFilterValue('forecastDetail', event.target.checked)}
                  />
                  <span className="checkmark" />
                  <span className="checkbox-label">Detalle de pronósticos</span>
                </label>
              </div>
            </div>
          </form>
          {error ? (
            <div className="form-error" role="alert">{error}</div>
          ) : null}
        </div>

        <UsageSummary summary={summary} filters={filters} />
      </div>

      <UsageTrends
        trends={trends}
        filters={filters}
        showDivisionChart={!filters.divisionId && !filters.areaId}
        showAreaChart={!filters.areaId}
      />

      <div className="metrics-card table-card">
        <div className="card-heading">
          <div>
            <h3 className="card-title">Tabla de actividad</h3>
          </div>
          <div className="table-heading-actions">
            <div className="pill-group compact">
              <span className="pill subtle">Total filas</span>
              <span className="pill strong">{filteredRows.length.toLocaleString()}</span>
            </div>
            <div className="table-tools">
              <div className="table-search">
                <input
                  type="search"
                  className="form-input"
                  placeholder="Buscar en la tabla"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>
              <button className="btn-secondary" type="button" onClick={handleTableExport} disabled={!paginatedRows.length}>
                <span className="btn-icon"><i data-lucide="download" /></span>
                Exportar CSV
              </button>
            </div>
          </div>
        </div>
        <div className="data-table-container usage-table-container">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner" />
              <div className="loading-text">Consultando métricas...</div>
            </div>
          ) : (
            <table className="data-table" id="usage-metrics-table">
              <thead>
                <tr>
                  {tableHeaders.map((header) => <th key={header}>{header}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={tableHeaders.length} className="empty-state">
                      <div className="empty-state-icon"><i data-lucide="chart-line" /></div>
                      <div className="empty-state-text">No hay datos para el rango seleccionado.</div>
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((row, index) => (
                    <tr key={index}>
                      {tableColumnsVisible.map((column) => (
                        <td key={column.key}>{column.getValue(row)}</td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
        {rows.length > rowsPerPage ? (
          <div className="table-pagination">
            <button
              className="btn-ghost"
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <span className="pagination-status">
              Página {currentPage} de {totalPages}
            </span>
            <button
              className="btn-ghost"
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Siguiente
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
};

const UsageSummary = ({ summary, filters }) => {
  const totalPronosticos = summary.articulos + summary.recetas;
  const recetasShare = totalPronosticos > 0 ? Math.round((summary.recetas / totalPronosticos) * 100) : 0;
  const cards = useMemo(() => ([
    {
      label: 'Clicks totales',
      value: summary.totalClicks.toLocaleString(),
      icon: 'mouse-pointer-2',
      accent: 'primary'
    },
    {
      label: 'Artículos consultados',
      value: summary.articulos.toLocaleString(),
      icon: 'package',
      accent: 'soft'
    },
    {
      label: 'Recetas consultadas',
      value: summary.recetas.toLocaleString(),
      icon: 'chef-hat',
      accent: 'soft'
    },
    {
      label: 'Participación recetas',
      value: `${recetasShare}%`,
      icon: 'pie-chart',
      accent: 'muted'
    }
  ]), [summary.totalClicks, summary.articulos, summary.recetas, recetasShare]);

  return (
    <div className="metrics-card summary-card">
      <div className="card-heading">
        <div>
          <div className="section-eyebrow">Resumen</div>
          <h3 className="card-title">Pulso inmediato</h3>
        </div>
        <div className="pill-group compact">
          <span className="pill subtle">Rango</span>
          <span className="pill strong">{filters.startDate} → {filters.endDate}</span>
        </div>
      </div>
      <div className="usage-summary-grid">
        {cards.map((card) => (
          <SummaryCard key={card.label} label={card.label} value={card.value} icon={card.icon} accent={card.accent} />
        ))}
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, icon, accent }) => (
  <div className={`summary-card metric-chip ${accent ? `metric-chip-${accent}` : ''}`}>
    <div className="summary-icon">
      <i data-lucide={icon} />
    </div>
    <div className="summary-content">
      <span className="summary-label">{label}</span>
      <span className="summary-value">{value}</span>
    </div>
  </div>
);

const UsageTrends = ({ trends, filters, showDivisionChart, showAreaChart }) => {
  const usersRef = useRef(null);
  const clicksRef = useRef(null);
  const perUserRef = useRef(null);
  const divisionRef = useRef(null);
  const areaRef = useRef(null);
  const processTypeRef = useRef(null);
  const hasProcessData = Boolean(
    (filters.includeArticulos && trends.processTypeBreakdown?.articulos?.length)
    || (filters.includeRecetas && trends.processTypeBreakdown?.recetas?.length)
  );

  useEffect(() => {
    const safeReact = (node, data, layout) => {
      if (!node || !node.parentNode) {
        return;
      }
      Plotly.react(node, data, layout, { displaylogo: false, responsive: true });
    };

    if (!usersRef.current || !clicksRef.current || !perUserRef.current) {
      return;
    }

    const baseLayout = {
      margin: { l: 40, r: 20, t: 20, b: 40 },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      font: { family: 'Sofia Pro, system-ui, sans-serif', size: 12, color: '#3a3f4b' },
      xaxis: { tickfont: { size: 11 }, showgrid: false },
      yaxis: { tickfont: { size: 11 }, gridcolor: 'rgba(58,63,75,0.08)' }
    };

    safeReact(
      usersRef.current,
      [
        {
          x: trends.labels,
          y: trends.uniqueUsers,
          type: 'scatter',
          mode: 'lines+markers+text',
          text: trends.uniqueUsers.map((value) => String(value)),
          textposition: 'top center',
          line: { color: '#c8102e', width: 2, shape: 'spline', smoothing: 1 },
          marker: { size: 6 }
        }
      ],
      { ...baseLayout, yaxis: { ...baseLayout.yaxis, rangemode: 'tozero' } }
    );

    safeReact(
      clicksRef.current,
      [
        {
          x: trends.labels,
          y: trends.totalClicks,
          type: 'scatter',
          mode: 'lines+markers+text',
          text: trends.totalClicks.map((value) => String(value)),
          textposition: 'top center',
          line: { color: '#2d3a4b', width: 2, shape: 'spline', smoothing: 1 },
          marker: { size: 6 }
        }
      ],
      { ...baseLayout, yaxis: { ...baseLayout.yaxis, rangemode: 'tozero' } }
    );

    const perUserSeries = trends.labels.map((_, index) => {
      const users = Number(trends.uniqueUsers[index] || 0);
      const clicks = Number(trends.totalClicks[index] || 0);
      if (!users) {
        return 0;
      }
      return Number((clicks / users).toFixed(2));
    });

    safeReact(
      perUserRef.current,
      [
        {
          x: trends.labels,
          y: perUserSeries,
          type: 'scatter',
          mode: 'lines+markers+text',
          text: perUserSeries.map((value) => String(value)),
          textposition: 'top center',
          line: { color: '#586174', width: 2, shape: 'spline', smoothing: 1 },
          marker: { size: 6 }
        }
      ],
      { ...baseLayout, yaxis: { ...baseLayout.yaxis, rangemode: 'tozero' } }
    );

    const safePurge = (node) => {
      if (node && node.parentNode) {
        Plotly.purge(node);
      }
    };

    return () => {
      safePurge(usersRef.current);
      safePurge(clicksRef.current);
      safePurge(perUserRef.current);
      safePurge(divisionRef.current);
      safePurge(areaRef.current);
      safePurge(processTypeRef.current);
    };
  }, [trends.labels, trends.totalClicks, trends.uniqueUsers]);

  useEffect(() => {
    const baseLayout = {
      margin: { l: 120, r: 20, t: 20, b: 40 },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      font: { family: 'Sofia Pro, system-ui, sans-serif', size: 12, color: '#3a3f4b' },
      xaxis: { tickfont: { size: 11 }, showgrid: false, rangemode: 'tozero' },
      yaxis: { tickfont: { size: 11 }, gridcolor: 'rgba(58,63,75,0.08)', autorange: 'reversed' },
      barmode: 'stack',
      showlegend: true,
      legend: { orientation: 'h', x: 0, y: 1.15 }
    };

    const safeReact = (node, data, layout) => {
      if (!node || !node.parentNode) {
        return;
      }
      Plotly.react(node, data, layout, { displaylogo: false, responsive: true });
    };

    let cleanupTargets = [];

    if (showDivisionChart && divisionRef.current) {
      const divisionRows = [...(trends.divisionBreakdown || [])].sort(
        (a, b) => Number(b.total_clicks || 0) - Number(a.total_clicks || 0)
      );
      const labels = divisionRows.map((row) => row.label || 'Sin división');
      const articulos = divisionRows.map((row) => Number(row.articulos_clicks || 0));
      const recetas = divisionRows.map((row) => Number(row.recetas_clicks || 0));
      const totals = divisionRows.map((row) => Number(row.total_clicks || 0));
      const height = Math.max(240, 80 + labels.length * 32);

      if (divisionRef.current) {
        divisionRef.current.style.height = `${height}px`;
      }
      cleanupTargets.push(divisionRef.current);
      safeReact(
        divisionRef.current,
        [
          {
            y: labels,
            x: articulos,
            name: 'Artículos',
            type: 'bar',
            orientation: 'h',
            marker: { color: '#f4b6c2' },
            text: articulos.map((value) => String(value)),
            textposition: 'outside'
          },
          {
            y: labels,
            x: recetas,
            name: 'Recetas',
            type: 'bar',
            orientation: 'h',
            marker: { color: '#c8102e' },
            text: recetas.map((value) => String(value)),
            textposition: 'inside',
            textfont: { color: '#ffffff', size: 11 }
          },
          {
            y: labels,
            x: totals,
            type: 'scatter',
            mode: 'text',
            text: totals.map((value) => String(value)),
            textposition: 'middle right',
            textfont: { color: '#3a3f4b', size: 11 },
            hoverinfo: 'skip',
            showlegend: false
          }
        ],
        { ...baseLayout, height }
      );
    }

    if (showAreaChart && areaRef.current) {
      const areaRows = [...(trends.areaBreakdown || [])].sort(
        (a, b) => Number(b.total_clicks || 0) - Number(a.total_clicks || 0)
      );
      const labels = areaRows.map((row) => row.label || 'Sin área');
      const articulos = areaRows.map((row) => Number(row.articulos_clicks || 0));
      const recetas = areaRows.map((row) => Number(row.recetas_clicks || 0));
      const totals = areaRows.map((row) => Number(row.total_clicks || 0));
      const height = Math.max(240, 80 + labels.length * 32);

      if (areaRef.current) {
        areaRef.current.style.height = `${height}px`;
      }
      cleanupTargets.push(areaRef.current);
      safeReact(
        areaRef.current,
        [
          {
            y: labels,
            x: articulos,
            name: 'Artículos',
            type: 'bar',
            orientation: 'h',
            marker: { color: '#f6cbd3' },
            text: articulos.map((value) => String(value)),
            textposition: 'outside'
          },
          {
            y: labels,
            x: recetas,
            name: 'Recetas',
            type: 'bar',
            orientation: 'h',
            marker: { color: '#d24159' },
            text: recetas.map((value) => String(value)),
            textposition: 'inside',
            textfont: { color: '#ffffff', size: 11 }
          },
          {
            y: labels,
            x: totals,
            type: 'scatter',
            mode: 'text',
            text: totals.map((value) => String(value)),
            textposition: 'middle right',
            textfont: { color: '#3a3f4b', size: 11 },
            hoverinfo: 'skip',
            showlegend: false
          }
        ],
        { ...baseLayout, height }
      );
    }

    const safePurge = (node) => {
      if (node && node.parentNode) {
        Plotly.purge(node);
      }
    };

    return () => {
      cleanupTargets.forEach((node) => safePurge(node));
    };
  }, [showDivisionChart, showAreaChart, trends.areaBreakdown, trends.divisionBreakdown]);

  useEffect(() => {
    const baseLayout = {
      margin: { l: 120, r: 20, t: 20, b: 40 },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      font: { family: 'Sofia Pro, system-ui, sans-serif', size: 12, color: '#3a3f4b' },
      xaxis: { tickfont: { size: 11 }, showgrid: false, rangemode: 'tozero' },
      yaxis: { tickfont: { size: 11 }, gridcolor: 'rgba(58,63,75,0.08)', autorange: 'reversed' },
      showlegend: false
    };

    const safeReact = (node, data, layout) => {
      if (!node || !node.parentNode) {
        return;
      }
      Plotly.react(node, data, layout, { displaylogo: false, responsive: true });
    };

    if (processTypeRef.current && trends.processTypeBreakdown) {
      const articulosData = trends.processTypeBreakdown.articulos || [];
      const recetasData = trends.processTypeBreakdown.recetas || [];
      const enabledTypes = [
        { key: 'articulos', label: 'Artículos', enabled: filters.includeArticulos, data: articulosData },
        { key: 'recetas', label: 'Recetas', enabled: filters.includeRecetas, data: recetasData }
      ].filter((type) => type.enabled);

      const hasData = enabledTypes.some((type) => type.data.length > 0);

      if (!enabledTypes.length || !hasData) {
        if (processTypeRef.current) {
          Plotly.purge(processTypeRef.current);
        }
        return;
      }

      const labels = enabledTypes.map((type) => type.label);
      const traces = [];

      const allTypes = new Set(enabledTypes.flatMap((type) => type.data.map((item) => item.label)));

      const colors = ['#c8102e', '#f4b6c2', '#d24159', '#586174', '#2d3a4b', '#e8a0ae', '#8b0000', '#ff6b6b'];

      Array.from(allTypes).forEach((processType, index) => {
        const counts = enabledTypes.map((type) => {
          const match = type.data.find((item) => item.label === processType);
          return match ? match.count || 0 : 0;
        });

        const color = colors[index % colors.length];

        traces.push({
          y: labels,
          x: counts,
          name: processType,
          type: 'bar',
          orientation: 'h',
          marker: { color: color },
          text: counts.map((value) => value > 0 ? String(value) : ''),
          textposition: 'inside',
          textfont: { color: '#ffffff', size: 11 },
          hovertemplate: `<b>${processType}</b><br>Cantidad: %{x}<extra></extra>`
        });
      });

      const height = 180;
      if (processTypeRef.current) {
        processTypeRef.current.style.height = `${height}px`;
      }

      safeReact(
        processTypeRef.current,
        traces,
        {
          ...baseLayout,
          height,
          barmode: 'stack',
          showlegend: true,
          legend: {
            orientation: 'h',
            x: 0,
            y: 1.2,
            font: { size: 10 }
          }
        }
      );
    }

    const safePurge = (node) => {
      if (node && node.parentNode) {
        Plotly.purge(node);
      }
    };

    return () => {
      safePurge(processTypeRef.current);
    };
  }, [filters.includeArticulos, filters.includeRecetas, trends.processTypeBreakdown]);

  const downloadPlot = async (node, filename) => {
    if (!node) {
      return;
    }
    try {
      const dataUrl = await Plotly.toImage(node, { format: 'png', height: 480, width: 960 });
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      // no-op; avoid blocking the UI if export fails
    }
  };

  return (
    <div className="metrics-card trends-card">
      <div className="card-heading">
        <div>
          <div className="section-eyebrow">Tendencias</div>
          <h3 className="card-title">Periodo filtrado</h3>
          <p className="card-subtitle">
            Evolución diaria de adopción y clics del {filters.startDate} al {filters.endDate}.
          </p>
        </div>
      </div>
      <div className="metrics-trends-grid">
        <div className="trend-panel">
          <div className="trend-header">
            <div className="trend-title">Usuarios únicos</div>
            <button
              className="btn-ghost btn-mini"
              type="button"
              onClick={() => downloadPlot(usersRef.current, 'usuarios_unicos.png')}
            >
              Descargar PNG
            </button>
          </div>
          <div className="trend-chart" ref={usersRef} />
        </div>
        <div className="trend-panel">
          <div className="trend-header">
            <div className="trend-title">Cantidad de clics</div>
            <button
              className="btn-ghost btn-mini"
              type="button"
              onClick={() => downloadPlot(clicksRef.current, 'cantidad_clics.png')}
            >
              Descargar PNG
            </button>
          </div>
          <div className="trend-chart" ref={clicksRef} />
        </div>
        <div className="trend-panel">
          <div className="trend-header">
            <div className="trend-title">Pronósticos por usuario</div>
            <button
              className="btn-ghost btn-mini"
              type="button"
              onClick={() => downloadPlot(perUserRef.current, 'pronosticos_por_usuario.png')}
            >
              Descargar PNG
            </button>
          </div>
          <div className="trend-chart" ref={perUserRef} />
        </div>
        {showDivisionChart ? (
          <div className="trend-panel">
            <div className="trend-header">
              <div className="trend-title">Pronósticos por división</div>
              {trends.divisionBreakdown?.length ? (
                <button
                  className="btn-ghost btn-mini"
                  type="button"
                  onClick={() => downloadPlot(divisionRef.current, 'pronosticos_division.png')}
                >
                  Descargar PNG
                </button>
              ) : null}
            </div>
            {trends.divisionBreakdown?.length ? (
              <div className="trend-chart dynamic-height" ref={divisionRef} />
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon"><i data-lucide="bar-chart-2" /></div>
                <div className="empty-state-text">Sin datos por división en el periodo.</div>
              </div>
            )}
          </div>
        ) : null}
        {showAreaChart ? (
          <div className="trend-panel">
            <div className="trend-header">
              <div className="trend-title">Pronósticos por área</div>
              {trends.areaBreakdown?.length ? (
                <button
                  className="btn-ghost btn-mini"
                  type="button"
                  onClick={() => downloadPlot(areaRef.current, 'pronosticos_area.png')}
                >
                  Descargar PNG
                </button>
              ) : null}
            </div>
            {trends.areaBreakdown?.length ? (
              <div className="trend-chart dynamic-height" ref={areaRef} />
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon"><i data-lucide="bar-chart" /></div>
                <div className="empty-state-text">Sin datos por área en el periodo.</div>
              </div>
            )}
          </div>
        ) : null}
        <div className="trend-panel">
          <div className="trend-header">
            <div className="trend-title">Procesos seleccionados</div>
            {hasProcessData ? (
              <button
                className="btn-ghost btn-mini"
                type="button"
                onClick={() => downloadPlot(processTypeRef.current, 'procesos_seleccionados.png')}
              >
                Descargar PNG
              </button>
            ) : null}
          </div>
          {hasProcessData ? (
            <div className="trend-chart dynamic-height" ref={processTypeRef} />
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon"><i data-lucide="layers" /></div>
              <div className="empty-state-text">Sin datos de procesos seleccionados en el periodo.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsageMetricsSection;
